import { Close, FilterList } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Divider,
  FormControl,
  Popper,
  Typography,
  useTheme,
} from "@mui/material";
import { PMButton, SearchBar } from "@pimo/pimo-components";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { useEffect, useMemo, useRef, useState } from "react";

import { FilterBar, FilterBarItem } from "./filter-bar";

export type FilterData = {
  searchQueryFilter: string[];
  oeFilter: string[];
  ownerFilter: string[];
  systemTypeFilter?: string[];
  distributionChannelFilter?: string[];
  lifeCycleFilter?: string[];
  criticalityFilter?: string[];
  reviewStatusFilter?: string[];
  realizesDomainsAggregatedFilter?: string[];
  eucFilter?: string[];
  dcRelevanceModifiedImportFilter?: string[];
  statusFilter: string[];
  completionFilter: string[];
  categoryFilter: string[];
  updateStatusFilter: string[];
};

const createDefaultFilterData = (): FilterData => ({
  searchQueryFilter: [],
  oeFilter: [],
  ownerFilter: [],
  systemTypeFilter: [],
  distributionChannelFilter: [],
  lifeCycleFilter: [],
  criticalityFilter: [],
  reviewStatusFilter: [],
  realizesDomainsAggregatedFilter: [],
  eucFilter: [],
  dcRelevanceModifiedImportFilter: [],
  statusFilter: [],
  completionFilter: [],
  categoryFilter: [],
  updateStatusFilter: [],
});

export type SearchAndFilterProps = {
  filterData?: FilterData;
  handleChange: (filterData: FilterData) => void;
  clearAllFilters?: () => void;
  filterLabel?: string;
  clearAllLabel?: string;
  moreFiltersLabel?: string;
  hideProminentInMoreFilters?: boolean;
  options?: {
    oe?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    owner?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    systemType?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    distributionChannel?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    lifeCycle?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    criticality?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    reviewStatus?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    realizesDomainsAggregated?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    euc?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    dcRelevanceModifiedImport?: {
      options?: string[];
      isEnabled?: boolean;
      label?: string;
    };
    status?: {
      options?: string[];
      isEnabled?: boolean;
    };
    completion?: {
      options?: string[];
      isEnabled?: boolean;
    };
    category?: {
      options?: string[];
      isEnabled?: boolean;
    };
    updateStatus?: {
      options?: string[];
      isEnabled?: boolean;
    };
  };
};

const filterConfigurations: Array<{
  id: keyof NonNullable<SearchAndFilterProps["options"]>;
  filterKey: keyof FilterData;
  defaultLabel: string;
}> = [
  { id: "oe", filterKey: "oeFilter", defaultLabel: "OE" },
  { id: "owner", filterKey: "ownerFilter", defaultLabel: "Owner" },
  {
    id: "systemType",
    filterKey: "systemTypeFilter",
    defaultLabel: "System Type",
  },
  {
    id: "distributionChannel",
    filterKey: "distributionChannelFilter",
    defaultLabel: "Distribution Channel",
  },
  {
    id: "lifeCycle",
    filterKey: "lifeCycleFilter",
    defaultLabel: "Life Cycle Status",
  },
  {
    id: "criticality",
    filterKey: "criticalityFilter",
    defaultLabel: "Criticality",
  },
  {
    id: "reviewStatus",
    filterKey: "reviewStatusFilter",
    defaultLabel: "Review Status",
  },
  {
    id: "realizesDomainsAggregated",
    filterKey: "realizesDomainsAggregatedFilter",
    defaultLabel: "Realizes (Domains Aggregated)",
  },
  { id: "euc", filterKey: "eucFilter", defaultLabel: "EUC" },
  {
    id: "dcRelevanceModifiedImport",
    filterKey: "dcRelevanceModifiedImportFilter",
    defaultLabel: "Modified by ADOIT import",
  },
  { id: "status", filterKey: "statusFilter", defaultLabel: "Status" },
  {
    id: "completion",
    filterKey: "completionFilter",
    defaultLabel: "Completion",
  },
  { id: "category", filterKey: "categoryFilter", defaultLabel: "Category" },
  {
    id: "updateStatus",
    filterKey: "updateStatusFilter",
    defaultLabel: "Update Status",
  },
];

const prominentFilterIds = new Set<
  keyof NonNullable<SearchAndFilterProps["options"]>
>(["owner", "distributionChannel", "euc", "reviewStatus"]);

const getFilterLabel = (
  filterId: keyof NonNullable<SearchAndFilterProps["options"]>,
  defaultLabel: string,
  options: SearchAndFilterProps["options"]
) => {
  const option = options?.[filterId];
  const configuredLabel =
    option && "label" in option ? option.label : undefined;

  if (filterId === "distributionChannel") {
    return configuredLabel || defaultLabel;
  }

  if (
    filterId === "status" ||
    filterId === "completion" ||
    filterId === "category" ||
    filterId === "updateStatus"
  ) {
    return defaultLabel;
  }

  return configuredLabel ?? defaultLabel;
};

export const SearchAndFilter = ({
  filterData,
  handleChange,
  clearAllFilters,
  filterLabel = "Filter",
  clearAllLabel = "Reset all filters",
  moreFiltersLabel = "More filters",
  hideProminentInMoreFilters = false,
  options,
}: SearchAndFilterProps) => {
  const theme = useTheme();
  const controlHeight = theme.spacing(5);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState<string>();

  const [filterDataState, setFilterDataState] = useState<FilterData>(
    filterData ?? createDefaultFilterData()
  );
  const [previousFilterData, setPreviousFilterData] = useState(filterData);

  if (filterData !== previousFilterData) {
    setPreviousFilterData(filterData);
    setFilterDataState(filterData ?? createDefaultFilterData());
  }

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFilterRef = useRef<FilterData | null>(null);

  const debouncedHandleChange = useMemo(() => {
    return (nextFilterData: FilterData) => {
      pendingFilterRef.current = nextFilterData;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (pendingFilterRef.current) {
          handleChange(pendingFilterRef.current);
        }
        debounceTimeoutRef.current = null;
      }, 500);
    };
  }, [handleChange]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const clickAwayHandler = () => {
    setOpen(false);
    setSelectedFilterId(undefined);
  };

  const openFilterMenu = (filterId: string, nextAnchorEl: HTMLElement) => {
    setSelectedFilterId(filterId);
    setOpen(true);
    setAnchorEl(nextAnchorEl);
  };

  const shouldRenderFilter = (
    filterId: keyof NonNullable<SearchAndFilterProps["options"]>
  ) =>
    selectedFilterId === undefined
      ? !hideProminentInMoreFilters || !prominentFilterIds.has(filterId)
      : selectedFilterId === filterId;
  const shouldOpenOptionsImmediately = selectedFilterId !== undefined;
  // A quick filter is only its option list, so closing the list closes the popper.
  const handleSelectClose = shouldOpenOptionsImmediately
    ? clickAwayHandler
    : undefined;

  const applyFilterUpdate = (updatedFilterData: FilterData) => {
    setFilterDataState(updatedFilterData);
    debouncedHandleChange(updatedFilterData);
  };

  const handleClearAll = () => {
    clearAllFilters?.();
    setOpen(false);
    setSelectedFilterId(undefined);
  };

  const activeFilterCount = filterConfigurations.reduce(
    (count, { filterKey }) => count + (filterDataState[filterKey]?.length ?? 0),
    0
  );

  const hasActiveFilters = activeFilterCount > 0;

  const enabledFilterCount = filterConfigurations.filter(
    ({ id }) => options?.[id]?.isEnabled
  ).length;
  const showMoreFilters = enabledFilterCount > 4;

  const filterBarItems: FilterBarItem[] = filterConfigurations
    .map(({ id, filterKey, defaultLabel }) => ({
      id,
      label: getFilterLabel(id, defaultLabel, options),
      activeCount: filterDataState[filterKey]?.length ?? 0,
      isEnabled: options?.[id]?.isEnabled,
    }))
    .filter(
      ({ id, activeCount, isEnabled }) =>
        isEnabled &&
        (!showMoreFilters || prominentFilterIds.has(id) || activeCount > 0)
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1.5,
        minWidth: 0,
        maxWidth: "100%",
        overflow: { xs: "hidden", lg: "visible" },
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          overflowX: { xs: "auto", lg: "visible" },
          overflowY: { xs: "hidden", lg: "visible" },
        }}
      >
        <FilterBar
          items={filterBarItems}
          clearAllLabel={clearAllLabel}
          moreFiltersLabel={moreFiltersLabel}
          showMoreFilters={showMoreFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilterId={selectedFilterId}
          handleFilterClick={openFilterMenu}
          handleMoreFiltersClick={(nextAnchorEl) => {
            setSelectedFilterId(undefined);
            setOpen(true);
            setAnchorEl(nextAnchorEl);
          }}
          handleClearAll={handleClearAll}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          height: controlHeight,
          border: "1px solid",
          borderRadius: "4px",
          borderColor: "#31384980",
          backgroundColor: "#FAFAFA",
          overflow: "hidden",
          "& .MuiInputBase-root": {
            height: "100%",
          },
          "& .MuiInputBase-input": {
            boxSizing: "border-box",
            height: "100%",
            paddingTop: 0,
            paddingBottom: 0,
          },
          "&:hover": {
            backgroundColor: "#FCFCFC",
          },
        }}
      >
        <SearchBar
          onChange={(value) => {
            const updatedFilterData = {
              ...filterDataState,
              searchQueryFilter: value ? [value] : [],
            };
            setFilterDataState(updatedFilterData);
            debouncedHandleChange(updatedFilterData);
          }}
        />
      </Box>
      {open && (
        <ClickAwayListener
          onClickAway={clickAwayHandler}
          mouseEvent={"onMouseUp"}
        >
          <Popper
            data-testid={"reportingDate-popover"}
            open={open}
            anchorEl={anchorEl}
            disablePortal={false}
            sx={{
              zIndex: theme.zIndex.modal,
              backgroundColor: shouldOpenOptionsImmediately
                ? "transparent"
                : "background.paper",
              borderColor: shouldOpenOptionsImmediately
                ? "transparent"
                : theme.palette.primary.main,
              boxShadow: shouldOpenOptionsImmediately
                ? "none"
                : "1px 1px 2px grey",
              borderRadius: shouldOpenOptionsImmediately ? 0 : "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: shouldOpenOptionsImmediately ? 0 : 1,
              maxHeight: "84vh",

              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: shouldOpenOptionsImmediately ? 0 : 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: shouldOpenOptionsImmediately ? 0 : 1,
                  gap: 0,
                  width: 300,
                }}
              >
                {!shouldOpenOptionsImmediately && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "start",
                          width: "100%",
                          pr: 1,
                        }}
                      >
                        <FilterList
                          sx={{
                            color: theme.palette.primary.main,
                            height: "35px",
                          }}
                        />
                        <Typography sx={{ fontWeight: 500 }}>
                          {filterLabel}
                        </Typography>
                      </Box>
                      <Close
                        sx={{ ":hover": { cursor: "pointer" } }}
                        onClick={clickAwayHandler}
                      ></Close>
                    </Box>
                    <Divider
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        height: "1px",
                        borderWidth: 0,
                      }}
                    />
                  </>
                )}
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    marginY: shouldOpenOptionsImmediately ? 0 : 2,
                    width: "100%",
                  }}
                >
                  {options?.oe?.isEnabled && shouldRenderFilter("oe") && (
                    <ControlledSelectField
                      options={options?.oe?.options ?? []}
                      value={filterDataState?.oeFilter ?? []}
                      label={options?.oe?.label ?? "OE"}
                      defaultOpen={shouldOpenOptionsImmediately}
                      hideControl={shouldOpenOptionsImmediately}
                      onClose={handleSelectClose}
                      multiple={true}
                      onChange={(newValue) => {
                        let result = [];
                        if (Array.isArray(newValue)) {
                          result = newValue as string[];
                        } else {
                          result = [newValue as string];
                        }
                        const updatedFilterData = {
                          ...filterDataState,
                          oeFilter: result,
                        };
                        applyFilterUpdate(updatedFilterData);
                      }}
                    ></ControlledSelectField>
                  )}

                  {options?.owner?.isEnabled && shouldRenderFilter("owner") && (
                    <ControlledSelectField
                      options={options?.owner?.options ?? []}
                      value={filterDataState?.ownerFilter ?? []}
                      label={options?.owner?.label ?? "Owner"}
                      defaultOpen={shouldOpenOptionsImmediately}
                      hideControl={shouldOpenOptionsImmediately}
                      onClose={handleSelectClose}
                      multiple={true}
                      onChange={(newValue) => {
                        let result = [];
                        if (Array.isArray(newValue)) {
                          result = newValue as string[];
                        } else {
                          result = [newValue as string];
                        }
                        const updatedFilterData = {
                          ...filterDataState,
                          ownerFilter: result,
                        };
                        applyFilterUpdate(updatedFilterData);
                      }}
                    ></ControlledSelectField>
                  )}

                  {options?.systemType?.isEnabled &&
                    shouldRenderFilter("systemType") && (
                      <ControlledSelectField
                        options={options?.systemType?.options ?? []}
                        value={filterDataState?.systemTypeFilter ?? []}
                        label={options?.systemType?.label ?? "System Type"}
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            systemTypeFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.distributionChannel?.isEnabled &&
                    shouldRenderFilter("distributionChannel") && (
                      <ControlledSelectField
                        options={options?.distributionChannel?.options ?? []}
                        value={filterDataState?.distributionChannelFilter ?? []}
                        label={
                          options?.distributionChannel?.label ||
                          "Distribution Channel"
                        }
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            distributionChannelFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.lifeCycle?.isEnabled &&
                    shouldRenderFilter("lifeCycle") && (
                      <ControlledSelectField
                        options={options?.lifeCycle?.options ?? []}
                        value={filterDataState?.lifeCycleFilter ?? []}
                        label={options?.lifeCycle?.label ?? "Life Cycle Status"}
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            lifeCycleFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.criticality?.isEnabled &&
                    shouldRenderFilter("criticality") && (
                      <ControlledSelectField
                        options={options?.criticality?.options ?? []}
                        value={filterDataState?.criticalityFilter ?? []}
                        label={options?.criticality?.label ?? "Criticality"}
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            criticalityFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.reviewStatus?.isEnabled &&
                    shouldRenderFilter("reviewStatus") && (
                      <ControlledSelectField
                        options={options?.reviewStatus?.options ?? []}
                        value={filterDataState?.reviewStatusFilter ?? []}
                        label={options?.reviewStatus?.label ?? "Review Status"}
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            reviewStatusFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.realizesDomainsAggregated?.isEnabled &&
                    shouldRenderFilter("realizesDomainsAggregated") && (
                      <ControlledSelectField
                        options={
                          options.realizesDomainsAggregated.options ?? []
                        }
                        value={
                          filterDataState.realizesDomainsAggregatedFilter ?? []
                        }
                        label={
                          options.realizesDomainsAggregated.label ??
                          "Realizes (Domains Aggregated)"
                        }
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          const result = Array.isArray(newValue)
                            ? (newValue as string[])
                            : [newValue as string];
                          applyFilterUpdate({
                            ...filterDataState,
                            realizesDomainsAggregatedFilter: result,
                          });
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.euc?.isEnabled && shouldRenderFilter("euc") && (
                    <ControlledSelectField
                      options={options.euc.options ?? []}
                      value={filterDataState.eucFilter ?? []}
                      label={options.euc.label ?? "EUC"}
                      defaultOpen={shouldOpenOptionsImmediately}
                      hideControl={shouldOpenOptionsImmediately}
                      onClose={handleSelectClose}
                      multiple={true}
                      onChange={(newValue) => {
                        const result = Array.isArray(newValue)
                          ? (newValue as string[])
                          : [newValue as string];
                        applyFilterUpdate({
                          ...filterDataState,
                          eucFilter: result,
                        });
                      }}
                    ></ControlledSelectField>
                  )}

                  {options?.dcRelevanceModifiedImport?.isEnabled &&
                    shouldRenderFilter("dcRelevanceModifiedImport") && (
                      <ControlledSelectField
                        options={
                          options.dcRelevanceModifiedImport.options ?? []
                        }
                        value={
                          filterDataState.dcRelevanceModifiedImportFilter ?? []
                        }
                        label={
                          options.dcRelevanceModifiedImport.label ??
                          "Modified by ADOIT import"
                        }
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          const result = Array.isArray(newValue)
                            ? (newValue as string[])
                            : [newValue as string];
                          applyFilterUpdate({
                            ...filterDataState,
                            dcRelevanceModifiedImportFilter: result,
                          });
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.status?.isEnabled &&
                    shouldRenderFilter("status") && (
                      <ControlledSelectField
                        options={options?.status?.options ?? []}
                        value={filterDataState?.statusFilter ?? []}
                        label="Status"
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            statusFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.completion?.isEnabled &&
                    shouldRenderFilter("completion") && (
                      <ControlledSelectField
                        options={options?.completion?.options ?? []}
                        value={filterDataState?.completionFilter ?? []}
                        label="Completion"
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            completionFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}
                  {options?.category?.isEnabled &&
                    shouldRenderFilter("category") && (
                      <ControlledSelectField
                        options={options?.category?.options ?? []}
                        value={filterDataState?.categoryFilter ?? []}
                        label="Category"
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            categoryFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}

                  {options?.updateStatus?.isEnabled &&
                    shouldRenderFilter("updateStatus") && (
                      <ControlledSelectField
                        options={options?.updateStatus?.options ?? []}
                        value={filterDataState?.updateStatusFilter ?? []}
                        label="Update Status"
                        defaultOpen={shouldOpenOptionsImmediately}
                        hideControl={shouldOpenOptionsImmediately}
                        onClose={handleSelectClose}
                        multiple={true}
                        onChange={(newValue) => {
                          let result = [];
                          if (Array.isArray(newValue)) {
                            result = newValue as string[];
                          } else {
                            result = [newValue as string];
                          }
                          const updatedFilterData = {
                            ...filterDataState,
                            updateStatusFilter: result,
                          };
                          applyFilterUpdate(updatedFilterData);
                        }}
                      ></ControlledSelectField>
                    )}
                </FormControl>
              </Box>
              {!shouldOpenOptionsImmediately && (
                <PMButton
                  size={"medium"}
                  variant={"contained"}
                  text={clearAllLabel}
                  onClick={handleClearAll}
                ></PMButton>
              )}
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
